import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEdit,
  faTrash,
  faBars,
  faChevronDown,
  faFileAlt,
  faSearch,
  faCheck,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { supabase } from '../lib/supabaseClient';
import AgreementModal from '../components/AgreementModal';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

function Agreements() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [agreements, setAgreements] = useState([]);
  const [filteredAgreements, setFilteredAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [agreementToDelete, setAgreementToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [agreementsPerPage, setAgreementsPerPage] = useState(10);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const fetchAgreements = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('agreements')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAgreements(data || []);
      setFilteredAgreements(data || []);
    } catch (error) {
      console.error('Error fetching agreements:', error);
      setError('Sözleşmeler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgreements();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const lowercasedQuery = searchQuery.toLowerCase();
      const filtered = agreements.filter(
        (agreement) =>
          agreement.title.toLowerCase().includes(lowercasedQuery) ||
          (agreement.en &&
            agreement.en.toLowerCase().includes(lowercasedQuery)) ||
          (agreement.tr &&
            agreement.tr.toLowerCase().includes(lowercasedQuery)) ||
          (agreement.ru && agreement.ru.toLowerCase().includes(lowercasedQuery))
      );
      setFilteredAgreements(filtered);
    } else {
      setFilteredAgreements(agreements);
    }
    setCurrentPage(1);
  }, [searchQuery, agreements]);

  const handleAddNew = () => {
    setSelectedAgreement(null);
    setIsModalOpen(true);
  };

  const handleEdit = (agreement) => {
    setSelectedAgreement(agreement);
    setIsModalOpen(true);
  };

  const openDeleteDialog = (agreement) => {
    setAgreementToDelete(agreement);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from('agreements')
        .delete()
        .eq('id', agreementToDelete.id);

      if (error) throw error;

      fetchAgreements();
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Error deleting agreement:', error);
      setError('Sözleşme silinirken bir hata oluştu');
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Pagination logic
  const indexOfLastAgreement = currentPage * agreementsPerPage;
  const indexOfFirstAgreement = indexOfLastAgreement - agreementsPerPage;
  const currentAgreements = filteredAgreements.slice(
    indexOfFirstAgreement,
    indexOfLastAgreement
  );

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const pageNumbers = [];
  for (
    let i = 1;
    i <= Math.ceil(filteredAgreements.length / agreementsPerPage);
    i++
  ) {
    pageNumbers.push(i);
  }

  const handleEntriesPerPageChange = (number) => {
    setAgreementsPerPage(number);
    setCurrentPage(1);
    setIsDropdownOpen(false);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Top Navigation */}
      <header className="bg-white shadow">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button className="text-gray-500 lg:hidden">
                <FontAwesomeIcon icon={faBars} className="text-xl" />
              </button>
            </div>

            <div className="relative">
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 focus:outline-none"
              >
                <span>{user?.email}</span>
                <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100">
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center justify-between pb-6">
            <div>
              <h1 className="text-2xl font-medium text-gray-800">
                Sözleşmeler
              </h1>
              <p className="text-sm text-gray-500">
                Toplam Sözleşme: {agreements.length}
              </p>
            </div>
            <button
              onClick={handleAddNew}
              className="bg-[#4263EB] text-white px-4 py-2 rounded-md hover:bg-[#3b5bd9] flex items-center"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Yeni Sözleşme Ekle
            </button>
          </div>

          {/* Filter Section */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sözleşme Ara
                </label>
                <input
                  type="text"
                  placeholder="Sözleşme başlığı veya içeriği ara..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Agreements Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {loading ? (
              <div className="p-4 text-center">Sözleşmeler yükleniyor...</div>
            ) : error ? (
              <div className="p-4 text-center text-red-500">{error}</div>
            ) : (
              <>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Başlık
                      </th>

                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Oluşturulma Tarihi
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        İşlemler
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentAgreements.map((agreement) => (
                      <tr key={agreement.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <FontAwesomeIcon
                              icon={faFileAlt}
                              className="text-gray-400 mr-2"
                            />
                            <div className="text-sm font-medium text-gray-900">
                              {agreement.title}
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {new Date(agreement.created_at).toLocaleDateString(
                              'tr-TR',
                              {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false,
                              }
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleEdit(agreement)}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                          >
                            <FontAwesomeIcon icon={faEdit} className="mr-1" />
                            Düzenle
                          </button>
                          <button
                            onClick={() => openDeleteDialog(agreement)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <FontAwesomeIcon icon={faTrash} className="mr-1" />
                            Sil
                          </button>
                        </td>
                      </tr>
                    ))}
                    {currentAgreements.length === 0 && (
                      <tr>
                        <td
                          colSpan="4"
                          className="px-6 py-4 text-center text-gray-500"
                        >
                          {searchQuery
                            ? 'Arama kriterlerine uygun sözleşme bulunamadı.'
                            : 'Henüz sözleşme eklenmemiş.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Pagination */}
                <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200">
                  <div className="text-sm text-gray-500">
                    Toplam {filteredAgreements.length} sözleşmeden{' '}
                    {indexOfFirstAgreement + 1}-
                    {Math.min(indexOfLastAgreement, filteredAgreements.length)}{' '}
                    arası gösteriliyor
                  </div>
                  <div className="flex-1 flex justify-end">
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                          currentPage === 1
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        <span className="sr-only">Previous</span>
                        <FontAwesomeIcon
                          icon={faChevronDown}
                          className="transform rotate-90"
                        />
                      </button>
                      {pageNumbers.map((number) => (
                        <button
                          key={number}
                          onClick={() => paginate(number)}
                          className={`relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium ${
                            currentPage === number
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {number}
                        </button>
                      ))}
                      <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={
                          currentPage === pageNumbers.length ||
                          pageNumbers.length === 0
                        }
                        className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                          currentPage === pageNumbers.length ||
                          pageNumbers.length === 0
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        <span className="sr-only">Next</span>
                        <FontAwesomeIcon
                          icon={faChevronDown}
                          className="transform -rotate-90"
                        />
                      </button>
                    </nav>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {/* Agreement Modal */}
      <AgreementModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        agreement={selectedAgreement}
        onSuccess={fetchAgreements}
      />

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full animate-fadeIn">
            <div className="flex items-center mb-4 text-red-600">
              <svg
                className="h-6 w-6 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              <h3 className="text-lg font-semibold">Sözleşmeyi Sil</h3>
            </div>
            <p className="mb-6 text-gray-600">
              <span className="font-medium text-gray-800">
                "{agreementToDelete.title}"
              </span>{' '}
              sözleşmesini silmek istediğinizden emin misiniz? Bu işlem geri
              alınamaz.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteDialog(false)}
                className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Agreements;
